import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

type TxnType = 'sale' | 'expense' | 'payment' | 'journal';

interface Transaction {
  id: string;
  type: TxnType;
  amount: number;
  currency: string;
  description: string;
  counterparty?: string;
  status: string;
  created_at: string;
}

const TABS: { key: TxnType; label: string }[] = [
  { key: 'sale', label: 'Sale' },
  { key: 'expense', label: 'Expense' },
  { key: 'payment', label: 'Payment' },
  { key: 'journal', label: 'Journal' },
];

export default function PastTransactions() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filtered, setFiltered] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState<TxnType>('sale');

  // Form state
  const [form, setForm] = useState({ amount: '', currency: 'NGN', description: '', counterparty: '', date: '' });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadTransactions(); }, []);
  useEffect(() => { filterTransactions(); }, [transactions, fromDate, toDate]);

  async function loadTransactions() {
    try {
      setLoading(true);
      const res = await api.get('/api/transactions');
      const list: Transaction[] = res.data ?? [];
      setTransactions(list);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function filterTransactions() {
    let list = [...transactions];
    if (fromDate) {
      const from = new Date(fromDate).getTime();
      list = list.filter((t) => new Date(t.created_at).getTime() >= from);
    }
    if (toDate) {
      const to = new Date(toDate).getTime() + 86400000;
      list = list.filter((t) => new Date(t.created_at).getTime() <= to);
    }
    setFiltered(list);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!form.amount || !form.description) {
      setFormError('Amount and description are required');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/transactions', {
        type: activeTab,
        amount: parseFloat(form.amount),
        currency: form.currency,
        description: form.description,
        counterparty: form.counterparty || null,
        transaction_date: form.date || new Date().toISOString().split('T')[0],
      });
      setForm({ amount: '', currency: 'NGN', description: '', counterparty: '', date: '' });
      setShowForm(false);
      loadTransactions();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const statusColor: Record<string, string> = {
    paid: '#166534',
    unpaid: '#92400e',
    draft: '#4b5563',
    pending: '#1d4ed8',
    confirmed: '#16a34a',
  };

  const statusBg: Record<string, string> = {
    paid: '#dcfce7',
    unpaid: '#fef3c7',
    draft: '#f3f4f6',
    pending: '#dbeafe',
    confirmed: '#dcfce7',
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={styles.heading}>Record Transaction</h1>
        <button onClick={() => setShowForm(!showForm)} style={styles.createBtn}>
          {showForm ? 'Cancel' : '+ Record Transaction'}
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {showForm && (
        <div style={styles.formCard}>
          <div style={styles.tabs}>
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                style={{
                  ...styles.tab,
                  ...(activeTab === t.key ? styles.tabActive : {}),
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {formError && <div style={styles.error}>{formError}</div>}
          <form onSubmit={submitForm} style={styles.form}>
            <div style={styles.formRow}>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} style={styles.input} />
              <input type="number" step="0.01" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={styles.input} required />
              <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={styles.input}>
                <option value="NGN">NGN</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <input type="text" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={styles.input} required />
            <input type="text" placeholder="Customer / Vendor / Counterparty (optional)" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} style={styles.input} />
            <button type="submit" disabled={saving} style={styles.saveBtn}>
              {saving ? 'Saving...' : `Save ${activeTab}`}
            </button>
          </form>
        </div>
      )}

      <div style={styles.filterRow}>
        <label style={styles.filterLabel}>From <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.filterInput} /></label>
        <label style={styles.filterLabel}>To <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={styles.filterInput} /></label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Counterparty</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id}>
                  <td style={styles.td}>{new Date(t.created_at).toLocaleDateString()}</td>
                  <td style={styles.td}><span style={typeBadge(t.type)}>{t.type}</span></td>
                  <td style={styles.td}>{t.description}</td>
                  <td style={styles.td}>{t.counterparty ?? '—'}</td>
                  <td style={styles.td}>{t.currency} {t.amount.toFixed(2)}</td>
                  <td style={styles.td}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 500,
                      color: statusColor[t.status] ?? '#4b5563',
                      background: statusBg[t.status] ?? '#f3f4f6',
                    }}>
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                    No transactions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function typeBadge(type: string): React.CSSProperties {
  const colors: Record<string, string> = {
    sale: '#16a34a',
    expense: '#dc2626',
    payment: '#2563eb',
    journal: '#7c3aed',
  };
  const bgs: Record<string, string> = {
    sale: '#dcfce7',
    expense: '#fee2e2',
    payment: '#dbeafe',
    journal: '#ede9fe',
  };
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    color: colors[type] ?? '#4b5563',
    background: bgs[type] ?? '#f3f4f6',
  };
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, margin: 0 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12 },
  createBtn: { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  formCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 20 },
  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  tabActive: { background: '#111827', color: '#fff', borderColor: '#111827' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  formRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  input: { padding: '10px 12px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 8, fontFamily: 'inherit', flex: 1, minWidth: 120 },
  saveBtn: { padding: 10, fontSize: 14, fontWeight: 500, background: '#111827', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' },
  filterRow: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  filterLabel: { fontSize: 13, color: '#6b7280' },
  filterInput: { display: 'block', marginTop: 4, padding: '8px 10px', fontSize: 13, border: '1px solid #e5e7eb', borderRadius: 6, fontFamily: 'inherit' },
  tableWrap: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid #e5e7eb', fontWeight: 600, color: '#6b7280', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid #f3f4f6' },
};
